import { AppConfigEntities, AppConfigState, AppMode } from './appConfig.reducer';
import { createFeatureSelector, createSelector, select } from '@ngrx/store';
import { filter } from 'rxjs/operators';
import { pipe } from 'rxjs';
import { getPagesEntities } from '../page/page.selectors';
import { ApplicationConfig } from 'shared/features/ng-redux/app/appConfig.models';
import * as fromProfiles from './../profiles/profiles.selector';
import * as fromPools from './../battery-pool/battery-pool.selector';
import * as fromWatchLists from './../watch-list/watchlist.selector';
import { WATCHLIST_CURRENT_APP } from '../../../../core/mappers/config-mapper/app-mapper.constants';
import * as fromUtilization from '../utilization/utilization.selector';

export const getAppConfigState = createFeatureSelector<AppConfigState>('applications');

export const getAppConfigEntites = createSelector(getAppConfigState, (appState: AppConfigState) => {
  return appState.entities;
});

export const getAppConfigMode = createSelector(getAppConfigState, (appState: AppConfigState) => {
  return appState.appMode;
});

export const getApplications = createSelector(getAppConfigEntites, (entities: AppConfigEntities) => {
  return Object.keys(entities).map(key => entities[key]);
});

export const lastPageAppMode = createSelector(getAppConfigState, (appState: AppConfigState) => {
  return appState.lastPageAppMode;
});

export const getIdsOrderedApplications = createSelector(getAppConfigState, (appState: AppConfigState) => {
  return appState.ids.map(key => appState.entities[key]);
});

export const getDashboardApplications = createSelector(getIdsOrderedApplications, (apps: ApplicationConfig[]) => {
  return apps;
});

export const getIsHydrated = createSelector(getAppConfigState, appState => appState.hydrated);

export const getIsLoadedSuccessfully = createSelector(
  getAppConfigState,
  getIsHydrated,
  (appState: AppConfigState, hydrated) => {
    if (appState.error) {
      return false;
    } else if (appState.ids && appState.ids.length > 0 && hydrated != undefined) {
      return true;
    }
  }
);

export const getMapsLoaded = createSelector(getAppConfigState, (appState: AppConfigState) => {
  return appState.mapsLoaded;
});

export const getShifts = createSelector(getAppConfigState, apps => apps.shifts);

export const getAppCategories = createSelector(getAppConfigState, apps => apps.appCategories);

export const getWebAppCategories = createSelector(getAppConfigState, apps => apps.webAppCategories);

export const getAppMappings = createSelector(getAppConfigState, apps => apps.appMappings);

export const getWebAppMappings = createSelector(getAppConfigState, apps => apps.webAppMappings);

export const getAppConfigByBaseLineId = (baseLineId: string) => {
  return createSelector(getApplications, apps => {
    const appConfig = apps.find(app => app.baselineId === baseLineId);
    if (appConfig) {
      return appConfig;
    }
  });
};

export const getAppConfigByBaseLineIdAndPages = (baseLineId: string) => {
  return createSelector(getApplications, getPagesEntities, (apps, pageEntities) => {
    const appConfig = apps.find(app => app.baselineId === baseLineId);
    if (appConfig) {
      const pageIds = appConfig.pageIds || [];
      const pages = pageIds.map(pageId => pageEntities[pageId]).filter(p => !!p);
      return { applicationConfiguration: appConfig, pages: pages };
    }
  });
};

export const getAppConfigById = (appId: string) => {
  return createSelector(getAppConfigEntites, (appConfigEntities: AppConfigEntities) => {
    return appConfigEntities[appId];
  });
};

export const getCurrentAppConfig = createSelector(getAppConfigState, (appState: AppConfigState) => {
  return appState.currentAppConfigId ? appState.entities[appState.currentAppConfigId] : null;
});

export const getCurrentAppConfigFiltered = pipe(
  select(getCurrentAppConfig),
  filter(res => !!res)
);

export const getAppsAndCurrentApp = createSelector(
  getApplications,
  getCurrentAppConfig,
  (apps: ApplicationConfig[], current: ApplicationConfig) => {
    return {
      applications: apps,
      currentApp: current,
    };
  }
);

export const isSmartBatterySelected = createSelector(getAppConfigState, (appState: AppConfigState) => {
  return appState.appMode === AppMode.BATTERIES;
});

export const isWatchlistCurrentSelected = createSelector(getAppConfigState, (appState: AppConfigState) => {
  return appState.currentAppConfigId.includes(WATCHLIST_CURRENT_APP);
});

export const getEntityId = createSelector(
  getAppConfigState,
  fromProfiles.getSelectedProfileId,
  fromPools.getSelectedBatteryPoolId,
  fromWatchLists.getSelectedWatchListId,
  fromUtilization.getSelectedUtilizationProfile,
  (app: AppConfigState, profileId: number, poolId: number, wlId: number, utilizationProfile) => {
    switch (app.appMode) {
      case AppMode.DEVICES:
        return profileId;
      case AppMode.BATTERIES:
        return poolId;
      case AppMode.WATCHLIST:
        return wlId;
      case AppMode.UTILIZATION:
        return utilizationProfile?.profileId;
    }
  }
);
